// Before/after slider: drag anywhere on the image, click to glide there, arrow keys on the handle.
const reducedMotion = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function bind(media: HTMLElement): void {
	const handle = media.querySelector<HTMLElement>('.armada-compare-hero__handle');

	if (!handle) {
		return;
	}

	let pos = parseFloat(media.style.getPropertyValue('--pos')) || 50;
	let frame = 0;
	let pending = pos;
	let dragging = false;
	let moved = false;
	let startX = 0;

	const apply = (): void => {
		frame = 0;
		pos = pending;
		media.style.setProperty('--pos', `${pos}%`);
		handle.setAttribute('aria-valuenow', String(Math.round(pos)));
		// Labels step aside when the divider runs over them.
		media.classList.toggle('is-before-hidden', pos < 18);
		media.classList.toggle('is-after-hidden', pos > 82);
	};

	const set = (value: number, smooth = false): void => {
		media.classList.toggle('is-gliding', smooth && !reducedMotion());
		pending = Math.max(0, Math.min(100, value));

		if (!frame) {
			frame = requestAnimationFrame(apply);
		}
	};

	const fromEvent = (event: PointerEvent): number => {
		const rect = media.getBoundingClientRect();

		return ((event.clientX - rect.left) / rect.width) * 100;
	};

	media.addEventListener('pointerdown', (event) => {
		if (event.button !== 0) {
			return;
		}

		dragging = true;
		moved = false;
		startX = event.clientX;
		media.setPointerCapture(event.pointerId);

		// Grabbing the handle keeps it under the finger; elsewhere the divider glides over.
		if (!handle.contains(event.target as Node)) {
			set(fromEvent(event), true);
		}
	});

	media.addEventListener('pointermove', (event) => {
		if (!dragging) {
			return;
		}

		// Only a real drag turns the glide off; a click keeps gliding to the spot.
		if (!moved && Math.abs(event.clientX - startX) > 3) {
			moved = true;
			media.classList.add('is-dragging');
		}

		if (moved) {
			set(fromEvent(event));
		}
	});

	const stop = (): void => {
		dragging = false;
		media.classList.remove('is-dragging');
	};

	media.addEventListener('pointerup', stop);
	media.addEventListener('pointercancel', stop);
	media.addEventListener('lostpointercapture', stop);

	handle.addEventListener('keydown', (event) => {
		const step = event.shiftKey ? 10 : 5;
		const keys: Record<string, number> = {
			ArrowLeft: pos - step,
			ArrowDown: pos - step,
			ArrowRight: pos + step,
			ArrowUp: pos + step,
			Home: 0,
			End: 100,
		};

		if (event.key in keys) {
			event.preventDefault();
				set(keys[event.key], true);
		}
	});

	media.addEventListener('transitionend', (event) => {
		if (event.target === media && event.propertyName === '--pos') {
			media.classList.remove('is-gliding');
		}
	});
}

export default function initCompare(): void {
	document.querySelectorAll<HTMLElement>('[data-armada-compare]').forEach(bind);
}
